import * as React from 'react';

import {Button} from 'shells/button/button';
import {Popover} from 'shells/popover/popover';

import type {NotifiableAliasType} from 'nutshell-graphql-types';

import {NotificationConfigModal} from './notification-config-modal';

import styles from './custom-notification-cell.module.css';

type User = {
    id: string;
    name: string;
};

type Assignee = {
    type: NotifiableAliasType;
};

type Props = {
    onSetNotification: (userIds: string[], assigneeValues: NotifiableAliasType[]) => void;
    notifiableUsers: User[];
    notifiableAssignees: Assignee[];
    shouldAllowEditingNotifiables: boolean;
};

export function CustomNotificationCell(props: Props) {
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [isShowingPopover, setIsShowingPopover] = React.useState<boolean>(false);

    const namesRef = React.useRef();
    const namesString = getNamesString(props.notifiableUsers, props.notifiableAssignees);

    return (
        <>
            <div tabIndex={isEditing ? undefined : 0}>
                <div className={styles['cell-content']}>
                    {namesString ? (
                        <>
                            <span
                                // @ts-expect-error
                                ref={namesRef}
                                onMouseOver={() => setIsShowingPopover(true)}
                                onMouseOut={() => setIsShowingPopover(false)}
                                className={styles.names}
                            >
                                {namesString}
                            </span>
                            {isShowingPopover ? (
                                <Popover anchor={namesRef.current} isDialog={true} bodyColor='dark'>
                                    {namesString}
                                </Popover>
                            ) : undefined}
                            {props.shouldAllowEditingNotifiables ? (
                                <Button
                                    variant='text-primary'
                                    size='normal'
                                    onClick={() => setIsEditing(true)}
                                >
                                    Edit
                                </Button>
                            ) : undefined}
                        </>
                    ) : undefined}
                    {!namesString && props.shouldAllowEditingNotifiables ? (
                        <Button
                            variant='text-primary'
                            size='normal'
                            onClick={() => setIsEditing(true)}
                        >
                            Add users
                        </Button>
                    ) : undefined}
                </div>
            </div>
            {isEditing ? (
                <NotificationConfigModal
                    onSetNotification={props.onSetNotification}
                    onCancel={() => setIsEditing(false)}
                    setIsEditing={setIsEditing}
                    notifiableUserIds={props.notifiableUsers.map((user) => user.id)}
                    notifiableAssigneeTypes={props.notifiableAssignees.map(
                        (assignee) => assignee.type
                    )}
                />
            ) : undefined}
        </>
    );
}

function getNamesString(
    users?: User[] | null,
    assignees?: Assignee[] | null
): string | null | undefined {
    const userNamesString =
        users && users.length > 0 ? users.map((user) => user.name).join(', ') : undefined;

    const assigneeString =
        assignees &&
        assignees
            .map((assignee) => {
                let string = '';

                if (assignee.type === 'CONTACT_ASSIGNEE') {
                    string = 'Contact assignee';
                }

                if (assignee.type === 'RELATED_LEAD_ASSIGNEE') {
                    string = 'Related lead assignee';
                }

                return string;
            })
            .join(', ');

    let namesString = assigneeString;

    if (assigneeString && userNamesString) {
        namesString = assigneeString.concat(', ', userNamesString);
    }

    if (!assigneeString && userNamesString) {
        namesString = userNamesString;
    }

    return namesString;
}
